import { create } from "zustand";

import { getSearchResultsForProd } from "../services/ElasticSearchAPI-prod";
import { getAppsSearchResults } from '../services/ElasticSearchAPI-APPS';
import { getCorrectedData } from "../util/store-utils";
import { dataParser } from "./tempDataParser";
import { getQuestionData, getSuggestionData } from "../services/chatBotApi";
import { EnvironmentConfig } from "@me8eon/config";
import { JwtTokenFn } from "@me8eon/authentication";
import { FavData, TabDataInterface, TileDataInterface } from "@me8eon/fav_storage";

const defaultAppTile = {
    synKeywords:
        "My genius, mygenius, learning, training, courses, upskilling, My genius, mygenius, Training, Weiterbildung, Lernangebote, Digitale Lernplattform",
    subTitle: "Digital Learning Platform",
    icon: "mygenius.svg",
    alias: "mygenius",
    model_id: ".multilingual-e5-small_linux-x86_64",
    title: "MyGenius",
    target: "https://eon.edcast.com/",
    index: EnvironmentConfig.appIndex,
    score: 0.9128927,
    status: true,
    cat: "Favourite",
    seq: 2,
};

export interface newAiQue {
    possible_user_question: string,
    allowed_all_companies?: boolean,
    allowed_all_countries?: boolean,
    allowed_all_users?: boolean
}

export interface que {
    title: string
}

interface SearchState {
    data: TileDataInterface[];
    questionairData: TileDataInterface[] | newAiQue[] | que[];
    loading: boolean;
    isPageLoading: boolean;
    error: string | null;
    prefError: string | null;
    isEditMode: boolean;
    isSearchFocused: boolean;
    displayedModal: string;
    currentTourId: string;
    favData: FavData;
    userId: string;
    showLiveAgentChat: boolean;
    allAppsData: TileDataInterface[];
    searchRequest: () => void;
    setUserId: (userId: string) => void;
    resultUpdate: (data: TileDataInterface[]) => void;
    searchFailure: (error: string) => void;
    showModal: (modal: string) => void;
    startTour: (tourId: string) => void;
    firstTourResponse: (response: string) => void;
    toggleEditMode: (isEditEnabled: boolean) => void;
    updateSearchData: () => void;
    updateFavObject: (data: {
        favApps: TileDataInterface[];
        favTabs: TabDataInterface[];
    }) => void;
    updateBannerData: (data: Record<string, string>) => void;
    toggleFavourite: (data: TileDataInterface, shouldFocusBack?: boolean) => void;
    addNewTab: (newTabArray: TabDataInterface[]) => void;
    fetchUserPref: (apiKey: () => Promise<string | undefined>, localAccountId: string | undefined) => void;
    setPrefData: (apiKey: () => Promise<string | undefined>, currentLang?: string) => void;
    fetchSearchData: (search: string, esApiKey: JwtTokenFn) => void;
    setSearchData: (data: any) => void;
    fetchAdvanceSearchData: (
        search: string,
        indicies: string[],
        language: string,
        esApiKey: JwtTokenFn,
    ) => void;
    fetchSuggestionData: (
        search: string,
        indicies: string[],
        esApiKey: JwtTokenFn,
        language: string
    ) => void;
    clearQuestionData: () => void;
    setQuestionData: (data: newAiQue[]) => void;
    setInputFocused: (focused: boolean) => void;
    setShowLiveAgentChat: (show: boolean) => void;
    getAllApps: (search: string, esApiKey: JwtTokenFn) => void;
    setAllApps: (data: TileDataInterface[]) => void;
}

const sessionCheck = localStorage.getItem("favData");
const sessionData = sessionCheck ? JSON.parse(sessionCheck) : {};

const initialState: Omit<
    SearchState,
    | "searchRequest"
    | "resultUpdate"
    | "searchFailure"
    | "showModal"
    | "startTour"
    | "firstTourResponse"
    | "toggleEditMode"
    | "updateFavObject"
    | "updateBannerData"
    | "updateSearchData"
    | "toggleFavourite"
    | "addNewTab"
    | "fetchUserPref"
    | "setPrefData"
    | "setInputFocused"
    | "fetchSearchData"
    | "setSearchData"
    | "fetchAdvanceSearchData"
    | "fetchSuggestionData"
    | "clearQuestionData"
    | "setQuestionData"
    | "setUserId"
    | "setShowLiveAgentChat"
    | "getAllApps"
    | "setAllApps"
> = {
    data: [],
    questionairData: [],
    loading: false,
    isPageLoading: false,
    error: null,
    prefError: null,
    isEditMode: false,
    isSearchFocused: false,
    displayedModal: "",
    currentTourId: "",
    userId: "dev_test_user", //should be kept empty once login issue is fixed
    favData: {
        favApps: [],
        favTabs: [],
        favLang: "en",
        tourTaken: false,
        ...sessionData,
    },
    showLiveAgentChat: false,
    allAppsData: [],
};

const useSearchStore = create<SearchState>((set, getState) => ({
    ...initialState,
    searchRequest: () => set({ loading: true }),
    setUserId: (userId) => set({ userId }),
    resultUpdate: (data) =>
        set((state) => {
            const dataWithStatus = data.map((resp: any) => {
                return {
                    ...resp,
                    status: state.favData.favApps.some(
                        (item) => item.alias === resp.alias,
                    ),
                };
            });
            return { loading: false, data: dataWithStatus };
        }),
    searchFailure: (error) => set({ loading: false, error }),
    showModal: (modal) => set({ displayedModal: modal }),
    startTour: (tourId) => set({ currentTourId: tourId }),
    setInputFocused: (focused: boolean) => set({ isSearchFocused: focused }),
    firstTourResponse: (response) =>
        set((state) => ({
            displayedModal: "",
            favData: {
                ...state.favData,
                tourTaken: response === "start" || response === "skip",
            },
        })),
    toggleEditMode: (isEditEnabled) => set({ isEditMode: isEditEnabled }),
    updateFavObject: (data) =>
        set((state) => ({ favData: { ...state.favData, ...data } })),
    updateBannerData: (data) =>
        set((state) => ({ favData: { ...state.favData, banner: data ?? state.favData.banner } })),
    updateSearchData: () =>
        set((state) => {
            const dataWithStatus = state.data.map((resp: any) => {
                return {
                    ...resp,
                    status: state.favData.favApps.some(
                        (item) => item.alias === resp.alias,
                    ),
                };
            });
            return { data: dataWithStatus };
        }),
    toggleFavourite: (tile, shouldFocusBack = true) =>
        set((state) => {
            const { alias } = tile;

            const appObj = state.favData.favApps.find(
                (obj) => obj.alias === alias,
            );

            let tabData = [...state.favData.favTabs];

            const activeTabName =
                tabData.find((tab) => tab.active)?.name || "Favourite";

            const itemExists = !!appObj;

            // remove count from app's own tab, add count to active tab
            const countTabName = itemExists
                ? appObj.cat || activeTabName
                : activeTabName;

            const existingFavLength = state.favData.favApps.filter(
                (obj) => obj.cat === activeTabName,
            ).length;

            const newFavApps = itemExists
                ? getCorrectedData(
                    state.favData.favApps,
                    alias,
                    appObj.cat || activeTabName,
                    appObj.seq || 0,
                )
                : [
                    ...state.favData.favApps,
                    {
                        ...tile,
                        cat: activeTabName,
                        seq: existingFavLength + 1,
                        status: true,
                    },
                ];

            const favTabNotExist = state.favData.favTabs.length === 0;

            let currentTourId = "";
            let isSearchFocused = shouldFocusBack;

            if (favTabNotExist) {
                tabData = [{ active: true, count: 1, name: "Favourite" }];
                currentTourId = "tour2";
                isSearchFocused = false;
            } else {
                const tabIndex = tabData.findIndex(
                    (obj) => obj.name === countTabName,
                );

                if (tabIndex >= 0) {
                    const existingCount = tabData[tabIndex]?.count || 0;

                    tabData[tabIndex] = {
                        ...tabData[tabIndex],
                        count: itemExists
                            ? Math.max(existingCount - 1, 0)
                            : existingCount + 1,
                    };
                }
            }

            return {
                favData: {
                    ...state.favData,
                    favTabs: tabData,
                    favApps: newFavApps,
                },
                currentTourId,
                isSearchFocused,
                data: state.data.map((obj) => ({
                    ...obj,
                    ...(obj.alias === alias && { status: !itemExists }),
                })),
            };
        }),
    addNewTab: (newTabArray) =>
        set((state) => ({
            favData: { ...state.favData, favTabs: newTabArray },
            displayedModal: "",
        })),
    fetchUserPref: async (apiKey: () => Promise<string | undefined>, localAccountId?: string) => {
        set({ isPageLoading: true });
        try {
            const url = `${EnvironmentConfig.nodeService}/service/prefData`;
            const requestData = {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${await apiKey()}`
                },
            };
            const data = await fetch(url, requestData);
            const dataJson = await data.json();

            const favDataResponse = dataJson.PrefData
                ? JSON.parse(dataJson.PrefData)
                : {};
            if (favDataResponse.favLang) {
                localStorage.setItem("i18nextLng", favDataResponse.favLang);
            }

            //temp code to support old fav apps
            const response = dataParser(favDataResponse);
            if (response.favApps.length < 1 && EnvironmentConfig.enableDefaultTileAppFeature) {
                response.favApps.push(defaultAppTile);
                response.favTabs.map((tab: TabDataInterface) => {
                    if (tab.name === "Favourite") {
                        tab.count = 1;
                    }
                    return tab;
                });
            }
            set((state) => ({
                isPageLoading: false,
                displayedModal: response.tourTaken ? "" : "firstTour",
                favData: { ...state.favData, ...response },
                userId: localAccountId,
            }));
        } catch (error: any) {
            set({ isPageLoading: false, displayedModal: "firstTour", prefError: error });
        }
    },
    setPrefData: async (apiKey: () => Promise<string | undefined>, currentLang?: string) => {
        const currentState = getState();
        const newData = currentLang
            ? { ...currentState.favData, favLang: currentLang }
            : currentState.favData;
        try {
            const url = `${EnvironmentConfig.nodeService}/service/prefData`;
            // const url = `http://localhost:3000/service/table`;
            const requestData = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${await apiKey()}`
                },
                body: JSON.stringify({
                    PrefData: JSON.stringify(newData),
                }),
            };

            const response = await fetch(url, requestData);
            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            const data = await response.json();
            if (data.isSuccess === "True") {
                console.log("saved successfully!");
            }
        } catch (error: any) {
            set({ isPageLoading: false, prefError: error });
        }
    },
    fetchSearchData: async (query: string, esApiKey: JwtTokenFn) => {
        set({ loading: true });
        const currentState = getState();
        try {
            const response = await getSearchResultsForProd(query, esApiKey);
            const dataWithStatus = response.map((resp: any) => {
                delete resp["full_text_embeddings"];
                return {
                    ...resp,
                    status: currentState.favData.favApps.some(
                        (item) => item.alias === resp.alias,
                    ),
                };
            });
            set({ loading: false, data: dataWithStatus });
        } catch (error: any) {
            set({ loading: false, error: error });
        }
    },
    setSearchData: (data: any) => {
        set({ data: data });
    },
    fetchAdvanceSearchData: async (
        query: string,
        indicies: string[],
        language: string,
        esApiKey: JwtTokenFn,
    ) => {
        if (indicies.length === 0) return;
        set({ loading: true });
        try {
            const response = await getQuestionData(query, indicies, esApiKey, language);
            set({ loading: false, questionairData: response });
        } catch (error: any) {
            set({ loading: false, error: error });
        }
    },
    fetchSuggestionData: async (
        query: string,
        indicies: string[],
        esApiKey: JwtTokenFn,
        language: string
    ) => {
        if (indicies.length === 0) return;
        set({ loading: true });
        try {
            const response = await getSuggestionData(query, indicies, esApiKey, language);
            set({ loading: false, questionairData: response });
        } catch (error: any) {
            set({ loading: false, error: error });
        }
    },
    clearQuestionData: () => {
        set({ questionairData: [] });
    },
    setQuestionData: (data: newAiQue[]) => {
        set({ questionairData: data });
    },
    setShowLiveAgentChat: (show: boolean) => {
        set({ showLiveAgentChat: show });
    },
    getAllApps: async (query: string, esApiKey: JwtTokenFn) => {
        set({ loading: true });
        const currentState = getState();
        try {
            const k = query.length ? 5 : 100;
            // const response = await getSearchResultsForProd(query, esApiKey, k);
            const response = await getAppsSearchResults(query, esApiKey);
            const appsData = response.map((resp: any) => {
                delete resp["full_text_embeddings"];
                return {
                    ...resp,
                    status: currentState.favData.favApps.some(
                        (item) => item.alias === resp.alias
                    ),
                    cat: "Favourite",
                    seq: 0
                };
            });
            set({ loading: false, allAppsData: appsData });
        } catch (e) {
            set({ loading: false });
            console.log("error", e);
        }
    },
    setAllApps: (appsData: TileDataInterface[]) => {
        set({ allAppsData: appsData });
    },
}));

export default useSearchStore;
